import { stripUnrecognizedShaarpTags } from "@/lib/ai";
import { stripCompanyMarkers } from "@/lib/company-mentions";
import { stripCitationMarkers } from "@shaarp/common";
import { normalizeQuotes } from "@/lib/string";
import { cn } from "@/lib/utils";
import { ChevronRight } from "lucide-react";
import { useMemo } from "react";
import { MessageResponse } from "./message";
import {
  EmailData,
  MessageResponseBlockEmail,
  MessageResponseBlockEmailSkeleton,
} from "./message-response-block-email";
import { Suggestion } from "./suggestion";

type SuggestionData = {
  label: string;
  prompt: string;
};

type ParsedPart =
  | { type: "text"; content: string }
  | { type: "suggestion"; data: SuggestionData }
  | { type: "email"; data: EmailData }
  | { type: "email-loading" };

// Remove incomplete tags at the end of text (during streaming)
function stripIncompleteTag(text: string): string {
  return text
    .replace(/\[@shaarp\/[^\]]*$/g, "")
    .replace(/\[@\/shaarp\/[^\]]*$/g, "");
}

// Check if an email block is currently being streamed (opening tag present, closing tag missing)
function findStreamingEmailBlock(text: string): number {
  const openMatches = [...text.matchAll(/\[@shaarp\/email[^\]]*\]/g)];
  const closeMatches = [...text.matchAll(/\[@\/shaarp\/email\]/g)];

  if (openMatches.length > closeMatches.length) {
    return openMatches[openMatches.length - 1]?.index ?? -1;
  }

  return -1;
}

// Parse text to extract email blocks
// Format: [@shaarp/email subject="..." to="..."]body content[@/shaarp/email]
function parseEmailBlocks(
  text: string
): { before: string; email: EmailData; after: string } | null {
  const normalizedText = normalizeQuotes(text);
  const emailRegex = /\[@shaarp\/email([^\]]*)\]([\s\S]*?)\[@\/shaarp\/email\]/;
  const match = normalizedText.match(emailRegex);

  if (!match) return null;

  const [, attributes = "", body = ""] = match;

  const subject = attributes.match(/subject="([^"]*)"/)?.[1] ?? "";
  const to = attributes.match(/to="([^"]*)"/)?.[1] ?? "";

  const [fullMatch] = match;
  const matchIndex = match.index!;

  // The text around the block is cut from the original: quotes are only
  // straightened to find the block (one character for one, so the indices
  // hold), never in what is shown — a company marker's « brief » stays whole.
  return {
    before: text.slice(0, matchIndex),
    email: {
      subject: subject.trim(),
      to: to.trim() || undefined,
      // The body renders in a plain textarea (and is copied/sent as-is), so
      // company markers must collapse to their name rather than leak raw syntax.
      body: stripCitationMarkers(stripCompanyMarkers(body)).trim(),
    },
    after: text.slice(matchIndex + fullMatch.length),
  };
}

// Parse text to extract suggestion tags
function parseSuggestions(
  text: string
): { before: string; suggestion: SuggestionData; after: string } | null {
  const normalizedText = normalizeQuotes(text);
  const regex =
    /\[@shaarp\/suggestion\s+label="([^"]+)"\s+prompt="([^"]+)"\]/;
  const match = normalizedText.match(regex);

  if (!match) return null;

  const [fullMatch, label, prompt] = match;
  const matchIndex = match.index!;

  // Cut from the original, as for an email block.
  return {
    before: text.slice(0, matchIndex),
    suggestion: { label, prompt },
    after: text.slice(matchIndex + fullMatch.length),
  };
}

// Parse text to extract all parts (text, emails, suggestions)
function parseTextWithBlocks(text: string, isStreaming: boolean): ParsedPart[] {
  // If streaming, check for email blocks being generated
  if (isStreaming) {
    const streamingEmailIndex = findStreamingEmailBlock(text);
    if (streamingEmailIndex !== -1) {
      const textBefore = text.slice(0, streamingEmailIndex);
      const parts: ParsedPart[] = [];

      if (textBefore.trim()) {
        parts.push(...parseTextWithBlocks(textBefore, false));
      }

      parts.push({ type: "email-loading" });
      return parts;
    }
  }

  let cleanText = isStreaming ? stripIncompleteTag(text) : text;
  cleanText = stripUnrecognizedShaarpTags(cleanText);

  const parts: ParsedPart[] = [];
  let remaining = cleanText;

  while (remaining.length > 0) {
    const emailMatch = parseEmailBlocks(remaining);
    if (emailMatch) {
      if (emailMatch.before.trim()) {
        parts.push({ type: "text", content: emailMatch.before.trim() });
      }
      parts.push({ type: "email", data: emailMatch.email });
      remaining = emailMatch.after;
      continue;
    }

    const suggestionMatch = parseSuggestions(remaining);
    if (suggestionMatch) {
      if (suggestionMatch.before.trim()) {
        parts.push({ type: "text", content: suggestionMatch.before.trim() });
      }
      parts.push({ type: "suggestion", data: suggestionMatch.suggestion });
      remaining = suggestionMatch.after;
      continue;
    }

    // No more special blocks — add remaining text as is
    const cleaned = remaining.trim();
    if (cleaned) {
      parts.push({ type: "text", content: cleaned });
    }
    break;
  }

  return parts;
}

export type MessageResponseWithBlocksProps = {
  children: string;
  onSuggestionClick?: (prompt: string) => void;
  disabled?: boolean;
  className?: string;
  animate?: boolean;
  isStreaming?: boolean;
  hideSuggestions?: boolean;
};

export function MessageResponseWithBlocks({
  children,
  onSuggestionClick,
  disabled = false,
  className,
  animate = true,
  isStreaming = false,
  hideSuggestions = false,
}: MessageResponseWithBlocksProps) {
  // When suggestions are hidden, pre-strip any `[@shaarp/suggestion ...]`
  // tag from input — permissively, so the raw tag never leaks even if the
  // attribute quoting is unusual. This is a no-op when hideSuggestions is
  // false, keeping behaviour identical for all existing callers.
  const processedChildren = useMemo(
    () =>
      hideSuggestions
        ? children.replace(/\[@shaarp\/suggestion\s+[^\]]*\]/g, "")
        : children,
    [children, hideSuggestions]
  );

  const parts = useMemo(
    () => parseTextWithBlocks(processedChildren, isStreaming),
    [processedChildren, isStreaming]
  );

  const hasSpecialBlocks = useMemo(
    () =>
      parts.some(
        (p) =>
          p.type === "suggestion" ||
          p.type === "email" ||
          p.type === "email-loading"
      ),
    [parts]
  );

  // If no special blocks found and not streaming, just render the text
  if (!hasSpecialBlocks && !isStreaming) {
    return (
      <MessageResponse className={className}>{processedChildren}</MessageResponse>
    );
  }

  let suggestionIndex = 0;

  return (
    <div data-slot="message-response-with-blocks" className={cn("space-y-4", className)}>
      {parts.map((part, index) => {
        if (part.type === "text") {
          return (
            <MessageResponse
              key={index}
              className="[&>*:first-child]:mt-0"
              animated={isStreaming}
              isAnimating={isStreaming}
            >
              {part.content}
            </MessageResponse>
          );
        }

        if (part.type === "email") {
          return (
            <MessageResponseBlockEmail
              key={index}
              email={part.data}
              className={cn(
                animate &&
                  "duration-300 animate-in slide-in-from-bottom-4 fade-in"
              )}
            />
          );
        }

        if (part.type === "email-loading") {
          return <MessageResponseBlockEmailSkeleton key={index} />;
        }

        // Suggestion
        const currentIndex = suggestionIndex++;
        const animationStyle = animate
          ? {
              animationDelay: `${currentIndex * 100}ms`,
              animationFillMode: "backwards" as const,
            }
          : undefined;

        return (
          <div
            key={index}
            className={cn(
              "flex justify-end",
              animate && "duration-300 animate-in slide-in-from-right-4 fade-in"
            )}
            style={animationStyle}
          >
            <Suggestion
              suggestion={part.data.prompt}
              onClick={() => onSuggestionClick?.(part.data.prompt)}
              disabled={disabled}
              className="text-xs gap-1.5"
            >
              {part.data.label}
              <ChevronRight className="size-3.5" />
            </Suggestion>
          </div>
        );
      })}
    </div>
  );
}
